import { getTheme } from "@clipboard-health/ui-theme";
import { Box, CircularProgress, Container, ThemeProvider } from "@mui/material";
import { useLegacyStateContext } from "@src/appV2/LegacyStateBridge/context";
import { useIsKeyboardOpen } from "@src/appV2/lib/utils/capacitor/useIsKeyboardOpen";
import { AnimatedBackgroundPageWrapper } from "@src/appV2/redesign/components/AnimatedBackgroundPageWrapper";
import { type ReactElement, useState } from "react";
import { Redirect, useLocation } from "react-router-dom";

import { AuthFooter } from "../components/Footer";
import { AuthHeadline } from "../components/Headline";
import { LoginSignUpForm } from "./Form/Form";
import { useLoginSignUpUiConfiguration } from "./hooks/useLoginSignUpUiConfiguration";
import { useSignOutForMagicLink } from "./hooks/useSignOutForMagicLink";

const fadeUp = {
  "@keyframes fadeUp": {
    from: { opacity: 0, transform: "translateY(12px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
};

export function LoginSignUpPage(): ReactElement {
  const { session } = useLegacyStateContext();
  const location = useLocation<{ from?: Location }>();
  const { hideFooterAndSecondaryButtonsOnKeyboardOpen } = useLoginSignUpUiConfiguration();
  const isKeyboardOpen = useIsKeyboardOpen();
  const [showAppVersionInfo, setShowAppVersionInfo] = useState(false);

  // Handle magic link sign-out before normal auth redirect
  const { isSigningOutForMagicLink, hasMagicLinkParameter } = useSignOutForMagicLink({
    isAuthorized: session.isAuthorized,
  });

  const shouldShowFooterAndSecondaryButtons = hideFooterAndSecondaryButtonsOnKeyboardOpen
    ? !isKeyboardOpen
    : true;

  // Show loading state while signing out for magic link
  if (isSigningOutForMagicLink) {
    return (
      <ThemeProvider theme={getTheme()}>
        <AnimatedBackgroundPageWrapper>
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              minHeight: "100%",
            }}
          >
            <CircularProgress sx={{ color: "white" }} />
          </Box>
        </AnimatedBackgroundPageWrapper>
      </ThemeProvider>
    );
  }

  // Only redirect if authorized AND there's no magic link param
  // (if there's a magic link param, we've just signed out and need to proceed with login)
  if (session.isAuthorized && !hasMagicLinkParameter) {
    const redirectPath = location.state?.from?.pathname ?? "/home";
    const redirectSearch = location.state?.from?.search ?? "";
    return <Redirect to={`${redirectPath}${redirectSearch}`} />;
  }

  return (
    <ThemeProvider theme={getTheme()}>
      <AnimatedBackgroundPageWrapper>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            minHeight: "100%",
            paddingY: 4,
            paddingX: 6,
            position: "relative",
            zIndex: 3,
          }}
        >
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              marginTop: 6,
            }}
          >
            <Container
              maxWidth="lg"
              sx={{
                ...fadeUp,
                animation: "fadeUp 700ms ease-out",
                display: "flex",
                flexDirection: "column",
                flex: 1,
              }}
            >
              <AuthHeadline
                onLogoSecretTap={() => {
                  setShowAppVersionInfo(true);
                }}
              />

              <Container
                maxWidth="md"
                sx={{
                  marginTop: 6,
                  display: "flex",
                  flexDirection: "column",
                  flex: 1,
                }}
              >
                <LoginSignUpForm />
              </Container>
            </Container>
          </Box>
          {shouldShowFooterAndSecondaryButtons && (
            <AuthFooter showAppVersionInfo={showAppVersionInfo} />
          )}
        </Box>
      </AnimatedBackgroundPageWrapper>
    </ThemeProvider>
  );
}
